import { 
  Input,
  OnInit,
  Component,
  ViewChild,
  forwardRef,
  HostBinding,
  TemplateRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { NgClass, NgTemplateOutlet } from '@angular/common';

/**
 * interfaces
 */
import { IInputType } from '@sharedModules/forms/interfaces/input-type';
import { IFormInputFieldUI } from '@sharedModules/forms/directives/form-input-field/interfaces/form-input-field';


/**
 * enums
 */
import { InputType } from '@sharedModules/forms/enums/input-type';

/**
 * models
*/
import { FormFieldHighOrderDirectiveBase } from '@sharedModules/forms/high-orders/form-field-high-order-directive';

/**
 * directives
 */
import { TemplateDirective } from '@sharedDirectives/templates/template.directive';
import { FormInputFieldDirective } from '@sharedModules/forms/directives/form-input-field/form-input-field.directive';
import { FormFieldStatusDirective } from '@sharedModules/forms/directives/form-field-status/form-field-status.directive';
import { FormFieldFeedbackDirective } from '@sharedModules/forms/directives/form-field-feedback/form-field-feedback.directive';

@Component({
  exportAs: 'inputFieldApi',
  selector: 'app-input-field',
  templateUrl: './input-field.component.html',
  styleUrl: './input-field.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.position-relative]': 'isFeedbackProviderTooltip'
  }, 
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputFieldComponent),
      multi: true
    }
  ],
  imports: [
    NgClass,
    NgTemplateOutlet,
    /**
     * directives
     */
    FormInputFieldDirective,
    FormFieldStatusDirective,
    FormFieldFeedbackDirective
  ],
})
export class InputFieldComponent extends FormFieldHighOrderDirectiveBase<string | number> implements IFormInputFieldUI, OnInit {

  /**
   * 
   */
  @Input({ alias: 'value' })
  public value: string | number = '';
  
  /**
   * @description get label templateRef
   */
  @ViewChild('labelTpl', { static: true, read: TemplateRef })
  private _fallbackLabelTpl!: TemplateRef<any>;

  /**
   * @description get inputField templateRef
   */
  @ViewChild('inputFieldTpl', { static: true, read: TemplateRef })
  private _fallbackInputFieldTpl!: TemplateRef<any>;

  /**
   * 
   */
  @HostBinding('class.form-floating')
  @Input({ alias: 'floating' })
  public isFloating: boolean = false

  /**
   * 
   */
  @Input({ alias: 'type' })
  public type: IInputType = InputType.Text

  /**
   * 
   */
  @Input({ alias: 'plaintext' })
  public isPlaintext : boolean = false

  /**
   * 
   */
  @Input({ alias: 'rounded' })
  public isRounded: boolean = false

  /**
   * 
   */
  @Input({ alias: 'min' })
  public min: number = 0

  /**
   * 
   */
  @Input({ alias: 'max' })
  public max: number | null = null

  /**
   * 
   */
  @Input({ alias: 'step' })
  public step: number = 0

  /**
   * 
   */
  @Input({ alias: 'valueAsNumber' })
  public valueAsNumber: boolean = false

  /**
   * 
   */
  public get isInputRange(): boolean | null {
    return this.type === InputType.Range ? true : null
  }

  /**
   * 
   */
  public get isIntegerInput(): boolean | null {
    return (this.type === InputType.Range || this.type === InputType.Number) ? true : null
  }  

  /**
   * 
   */
  public get isInputColor(): boolean {
    return this.type === InputType.Color
  }

  /** 
   * @description Returns the first block to render in the form layout
   */
  public get topSectionTpl(): TemplateRef<any> {
    return this.isFloating ? this._inputFieldTplRef : this._labelTplRef;
  }

  /** 
   * @description Returns the second block to render in the form layout
   */
  public get bottomSection(): TemplateRef<any> {
    return this.isFloating ? this._labelTplRef : this._inputFieldTplRef
  }

  /**
   * 
   */
  private get _inputFieldTplRef(): TemplateRef<any> {
    return this._inputFieldTplDirective?.templateRef ?? this._fallbackInputFieldTpl
  }

  /**
   * 
   */
  private get _labelTplRef(): TemplateRef<any> {
    return this._labelTplDirective?.templateRef ?? this._fallbackLabelTpl
  }

  /**
   * 
   */
  private get _inputFieldTplDirective(): TemplateDirective | undefined {
    return this.getTemplate('inputField')
  }

  /**
   * 
   */
  private get _labelTplDirective(): TemplateDirective | undefined {
    return this.getTemplate('label')
  }

  /**
   * 
   */
  public get templateContext() {
    return {
      id: this.id,
      label: this.label,
      value: this.value,
      valid: this.valid,
      touched: this.touched,
      invalid: this.invalid,
      disabled: this.disabled,
      placeholder: this.placeholder,
    }
  }

  /**
   *
   */
  constructor() {
    super();
    
  }

  /**
   * 
   */
  public onInputChange(event: Event): void {
    
    // get element
    // ES6 Destruction
    const { value, valueAsNumber } = event.target as HTMLInputElement;

    // update inner value
    this.value = !this.valueAsNumber ? value : (valueAsNumber ? valueAsNumber : '');

    // update control value
    if (this.control != null) {
      this.onChange(this.value);
    }

    // emit eventEmitter
    this.onChangeEvent.emit(this.value);

  }

  /**
   * 
   */
  public onInputTouched(): void {

    // update control status 
    if (this.control != null) {
      this.onTouched();
    }
    
  }

}
